import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Briefcase,
  CheckSquare,
  FileText,
  BarChart3,
  Settings,
  HelpCircle,
  ChevronLeft,
  LogOut,
  Calendar,
  UserCog,
  AlertTriangle
} from 'lucide-react'
import { useAuth } from '../../contexts/auth-context'
import { useFirm } from '../../hooks/use-firm'
import './layout.css'

interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Users, label: 'Clients', path: '/clients' },
  { icon: UserCheck, label: 'Taxpayers', path: '/taxpayers' },
  { icon: Briefcase, label: 'Engagements', path: '/engagements' },
  { icon: Calendar, label: 'Filings', path: '/filings' },
  { icon: CheckSquare, label: 'Tasks', path: '/tasks' },
  { icon: FileText, label: 'Documents', path: '/documents' },
  { icon: BarChart3, label: 'Reports', path: '/reports' },
  { icon: UserCog, label: 'Team Members', path: '/staff' },
  { icon: AlertTriangle, label: 'Risk', path: '/risk', requireManager: true },
]

const bottomItems = [
  { icon: Settings, label: 'Settings', path: '/settings' },
  { icon: HelpCircle, label: 'Help', path: '/help' },
]

export function Sidebar({ collapsed = false, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const { user, signOut } = useAuth()
  const { userRole, isManagerOrAbove } = useFirm()

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Sign out failed:', error)
    }
  }

  const handleNavClick = () => {
    onMobileClose?.()
  }

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      {/* Logo */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img src="/logo.png" alt="SzB Certified Public Accountants" className="sidebar-logo-img" />
        </div>
        {onToggle && (
          <button
            onClick={onToggle}
            className="sidebar-toggle"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeft size={18} />
          </button>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="sidebar-nav" role="navigation" aria-label="Main navigation">
        <ul className="sidebar-nav-list">
          {navItems
            .filter((item) => !('requireManager' in item) || isManagerOrAbove())
            .map((item) => (
              <li key={item.path} onClick={handleNavClick}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-nav-item ${isActive ? 'active' : ''}`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon size={20} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
        </ul>
      </nav>

      {/* Bottom Section */}
      <div className="sidebar-bottom">
        <ul className="sidebar-nav-list">
          {bottomItems.map((item) => (
            <li key={item.path} onClick={handleNavClick}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? 'active' : ''}`
                }
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={20} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* User Profile */}
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          {!collapsed && (
            <div className="sidebar-user-info">
              <span className="sidebar-user-email">{user?.email}</span>
              <span className={`sidebar-user-role badge-${userRole}`}>
                {userRole}
              </span>
            </div>
          )}
          <button
            onClick={handleSignOut}
            className="sidebar-logout"
            title="Sign out"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  )
}
